import { useState, useRef } from "react";
import Grid from "@mui/material/Grid";
import { Card } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  IoCloudUploadOutline, IoCheckmarkCircle, IoCloseCircle,
  IoWarning, IoInformationCircle, IoSparkles, IoTimeOutline, IoCashOutline,
} from "react-icons/io5";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const SEVERITY_CONFIG = {
  Critical: { color: "#FC8181", bg: "rgba(252,129,129,0.12)", icon: IoCloseCircle },
  Warning:  { color: "#C9A84C", bg: "rgba(201,168,76,0.12)",  icon: IoWarning },
  Info:     { color: "#4FD1C5", bg: "rgba(79,209,197,0.12)",  icon: IoInformationCircle },
};

const CAPABILITIES = [
  "Unmerge cells & fill down values",
  "Remove blank rows & columns",
  "Strip color formatting",
  "Unhide hidden rows & columns",
  "Remove broken formulas",
  "Numbers stored as text → convert",
  "Trim whitespace from all cells",
  "Duplicate rows → remove",
  "Blank / invalid account codes",
  "Dr/Cr imbalance detection",
  "Round-number anomaly flags",
  "Date format standardisation",
  "Closing balance mismatch",
  "Zero-value ghost rows",
];

function KpiCard({ label, value, color, sub }) {
  return (
    <Card>
      <VuiBox textAlign="center">
        <VuiTypography variant="caption" color="text" display="block" mb={0.5}>{label}</VuiTypography>
        <VuiTypography variant="h4" fontWeight="bold" sx={{ color }}>{value}</VuiTypography>
        {sub && <VuiTypography variant="caption" color="text" display="block" mt={0.5}>{sub}</VuiTypography>}
      </VuiBox>
    </Card>
  );
}

function IssueRow({ issue }) {
  const cfg = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.Info;
  const Icon = cfg.icon;
  return (
    <VuiBox mb={2} p={2} sx={{ background: cfg.bg, borderLeft: `3px solid ${cfg.color}`, borderRadius: "10px" }}>
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <VuiBox display="flex" alignItems="center" gap="8px">
          <Icon color={cfg.color} size="16px" />
          <VuiTypography variant="button" fontWeight="bold" sx={{ color: cfg.color }}>{issue.type}</VuiTypography>
        </VuiBox>
        <VuiBox px={1.5} py={0.25} sx={{ background: cfg.color + "33", borderRadius: "20px" }}>
          <VuiTypography variant="caption" sx={{ color: cfg.color }} fontWeight="bold">
            {issue.severity} · {issue.count} {issue.count === 1 ? "row" : "rows"}
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <VuiTypography variant="caption" color="text" display="block" mb={issue.ai_explanation ? 1 : 0}>
        {issue.detail}
      </VuiTypography>

      {issue.ai_explanation && (
        <VuiBox display="flex" alignItems="flex-start" gap="6px" mt={0.5} p={1}
          sx={{ background: "rgba(255,255,255,0.04)", borderRadius: "6px" }}>
          <IoSparkles color="#C9A84C" size="13px" style={{ marginTop: "2px", flexShrink: 0 }} />
          <VuiTypography variant="caption" sx={{ color: "#C9A84C", fontStyle: "italic" }}>
            {issue.ai_explanation}
          </VuiTypography>
        </VuiBox>
      )}

      {issue.rows && issue.rows.length > 0 && (
        <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
          Affected rows: {issue.rows.join(", ")}{issue.rows.length === 10 ? "..." : ""}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

function FilterTab({ label, count, color, active, onClick }) {
  return (
    <VuiBox
      onClick={onClick}
      px={2} py={0.75}
      sx={{
        cursor: "pointer",
        borderRadius: "8px",
        background: active ? `${color}22` : "transparent",
        border: `1px solid ${active ? color : "transparent"}`,
        transition: "all 0.2s",
        display: "flex", alignItems: "center", gap: "6px",
      }}
    >
      <VuiTypography variant="caption" fontWeight="bold" sx={{ color: active ? color : "#56577A" }}>
        {label}
      </VuiTypography>
      <VuiBox px={1} sx={{ background: active ? color : "#56577A", borderRadius: "10px" }}>
        <VuiTypography variant="caption" color="white" fontWeight="bold" sx={{ fontSize: "10px" }}>{count}</VuiTypography>
      </VuiBox>
    </VuiBox>
  );
}

function PreviewTable({ rows, highlight }) {
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  const tdBase = {
    padding: "5px 10px", fontSize: "11px", whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#a0aec0",
  };
  return (
    <VuiBox sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c} style={{ ...tdBase, color: "#fff", fontWeight: 700, background: "rgba(0,117,255,0.15)", textAlign: "left" }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              {cols.map((c) => (
                <td key={c} style={{ ...tdBase, color: highlight ? "#48BB78" : "#a0aec0" }}>
                  {row[c]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </VuiBox>
  );
}

function BeforeAfter({ original, cleaned }) {
  if (!original || !cleaned) return null;
  return (
    <Card sx={{ mt: 3 }}>
      <VuiBox>
        <VuiTypography variant="lg" color="white" fontWeight="bold" mb={2}>
          Before / After Preview
        </VuiTypography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <VuiBox mb={1} display="flex" alignItems="center" gap={1}>
              <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#FC8181" }} />
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: "#FC8181" }}>Original (messy)</VuiTypography>
            </VuiBox>
            <VuiBox sx={{ border: "1px solid rgba(252,129,129,0.3)", borderRadius: "10px", overflow: "hidden" }}>
              <PreviewTable rows={original} highlight={false} />
            </VuiBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <VuiBox mb={1} display="flex" alignItems="center" gap={1}>
              <VuiBox sx={{ width: 10, height: 10, borderRadius: "50%", background: "#48BB78" }} />
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: "#48BB78" }}>Cleaned (fixed)</VuiTypography>
            </VuiBox>
            <VuiBox sx={{ border: "1px solid rgba(72,187,120,0.3)", borderRadius: "10px", overflow: "hidden" }}>
              <PreviewTable rows={cleaned} highlight={true} />
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
    </Card>
  );
}

function DataCleaner() {
  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState("");
  const [filter, setFilter]     = useState("All");
  const fileRef                 = useRef();

  function handleFile(f) {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) { setError("Upload .xlsx, .xls, or .csv"); return; }
    setFile(f); setError(""); setResult(null); setFilter("All");
  }

  async function handleClean() {
    if (!file) return;
    setLoading(true); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("company", "Suntech");
      const res = await fetch(`${API_URL}/clean`, { method: "POST", body: form });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server error: ${res.status}`);
      }
      const data = await res.json();
      setResult(data);
      setFilter("All");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleDownload() {
    if (!result?.clean_file_b64) return;
    const binary = atob(result.clean_file_b64);
    const bytes  = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "Cleaned_Data.xlsx"; a.click();
    URL.revokeObjectURL(url);
  }

  const critical = result?.critical_count || 0;
  const warning  = result?.warning_count  || 0;
  const info     = result?.info_count     || 0;
  const total    = result?.total_issues   || 0;

  const timeSaved = result?.time_saved > 0 ? `${result.time_saved} min` : "—";

  const filteredIssues = (result?.issues || []).filter(
    (i) => filter === "All" || i.severity === filter
  );

  const FILTERS = [
    { label: "All",      count: total,    color: "#0075FF" },
    { label: "Critical", count: critical, color: "#FC8181" },
    { label: "Warning",  count: warning,  color: "#C9A84C" },
    { label: "Info",     count: info,     color: "#4FD1C5" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>

        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">AI Data Cleaner</VuiTypography>
          <VuiTypography variant="button" color="text">
            Upload any Excel or CSV — sales reports, GL, invoices, HR data. AI finds and fixes issues, delivers a clean file in seconds.
          </VuiTypography>
        </VuiBox>

        <Grid container spacing={3}>

          {/* ── Left panel ── */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: "100%" }}>
              <VuiBox display="flex" flexDirection="column" gap={2}>
                <VuiTypography variant="lg" color="white" fontWeight="bold">Upload File</VuiTypography>

                {/* Drop zone */}
                <VuiBox
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
                  onClick={() => fileRef.current.click()}
                  sx={{
                    border: `2px dashed ${file ? "#48BB78" : dragging ? "#0075FF" : "#56577A"}`,
                    borderRadius: "16px", p: 4, textAlign: "center", cursor: "pointer",
                    background: file ? "rgba(72,187,120,0.08)" : dragging ? "rgba(0,117,255,0.08)" : "rgba(255,255,255,0.02)",
                    transition: "all 0.2s", minHeight: "140px",
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  }}
                >
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }}
                    onChange={(e) => handleFile(e.target.files[0])} />
                  <IoCloudUploadOutline size="40px" color={file ? "#48BB78" : "#56577A"} />
                  <VuiTypography variant="h6" color="white" mt={1.5} fontSize="14px">
                    {file ? `✓ ${file.name}` : "Drop your file here"}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">Sales reports · GL · Invoices · HR · Any Excel · CSV</VuiTypography>
                </VuiBox>

                {error && <VuiTypography variant="caption" color="error">⚠ {error}</VuiTypography>}

                <VuiButton color="info" variant="contained" fullWidth onClick={handleClean} disabled={!file || loading}>
                  {loading ? "Analysing with AI..." : "⚡ Clean & Analyse"}
                </VuiButton>

                {result && (
                  <VuiButton color="success" variant="contained" fullWidth onClick={handleDownload}>
                    ⬇ Download Clean File
                  </VuiButton>
                )}

                {/* Capabilities list */}
                <VuiBox mt={1} p={2} sx={{ background: "rgba(255,255,255,0.03)", borderRadius: "10px" }}>
                  <VuiTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                    DETECTS & FIXES
                  </VuiTypography>
                  {CAPABILITIES.map((t) => (
                    <VuiBox key={t} display="flex" alignItems="center" gap="6px" mb="4px">
                      <IoCheckmarkCircle size="11px" color="#48BB78" />
                      <VuiTypography variant="caption" color="text">{t}</VuiTypography>
                    </VuiBox>
                  ))}
                </VuiBox>
              </VuiBox>
            </Card>
          </Grid>

          {/* ── Right panel ── */}
          <Grid item xs={12} lg={8}>

            {/* Empty state */}
            {!result && !loading && (
              <Card sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
                <VuiBox textAlign="center">
                  <IoSparkles size="48px" color="#56577A" />
                  <VuiTypography variant="h6" color="text" mt={2}>Upload a file to see AI analysis</VuiTypography>
                  <VuiTypography variant="caption" color="text">Issues, summary and clean file will appear here</VuiTypography>
                </VuiBox>
              </Card>
            )}

            {/* Loading state */}
            {loading && (
              <Card sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
                <VuiBox textAlign="center">
                  <IoSparkles size="40px" color="#C9A84C" />
                  <VuiTypography variant="h6" color="white" mt={2} mb={1}>Analysing your data...</VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    Unmerging cells · Fixing formats · Running AI · Preparing clean file
                  </VuiTypography>
                  {/* Animated progress bar */}
                  <VuiBox mt={3} sx={{ width: "200px", height: "4px", background: "#1A1D3A", borderRadius: "2px", mx: "auto", overflow: "hidden" }}>
                    <VuiBox sx={{
                      height: "100%", background: "linear-gradient(90deg, #0075FF, #C9A84C)",
                      borderRadius: "2px", width: "60%",
                      animation: "slide 1.2s ease-in-out infinite alternate",
                      "@keyframes slide": { from: { marginLeft: "0%" }, to: { marginLeft: "40%" } }
                    }} />
                  </VuiBox>
                </VuiBox>
              </Card>
            )}

            {/* Results */}
            {result && (
              <VuiBox display="flex" flexDirection="column" gap={3}>

                {/* KPI row */}
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <KpiCard label="Total Rows"  value={result.total_rows} color="#0075FF" sub="uploaded" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KpiCard label="Issues Found" value={total} color={critical > 0 ? "#FC8181" : "#C9A84C"} sub={`${critical} critical`} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KpiCard label="Time Saved"  value={timeSaved} color="#4FD1C5" sub="vs manual fix" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <KpiCard label="Clean Rows"  value={result.clean_rows} color="#48BB78" sub="ready to use" />
                  </Grid>
                </Grid>

                {/* Time saved bar */}
                {total > 0 && timeSaved !== "—" && (
                  <VuiBox p={2} sx={{ background: "rgba(79,209,197,0.1)", borderRadius: "12px", border: "1px solid rgba(79,209,197,0.25)", display: "flex", alignItems: "center", gap: "10px" }}>
                    <IoTimeOutline color="#4FD1C5" size="22px" />
                    <VuiBox>
                      <VuiTypography variant="caption" color="text" display="block">Estimated Manual Fix Time Saved</VuiTypography>
                      <VuiTypography variant="button" fontWeight="bold" sx={{ color: "#4FD1C5" }}>{timeSaved} saved</VuiTypography>
                    </VuiBox>
                  </VuiBox>
                )}

                {/* AI Executive Summary */}
                {result.ai_summary && (
                  <Card>
                    <VuiBox display="flex" gap={2} alignItems="flex-start">
                      <IoSparkles color="#C9A84C" size="20px" style={{ flexShrink: 0, marginTop: "2px" }} />
                      <VuiBox>
                        <VuiTypography variant="button" fontWeight="bold" sx={{ color: "#C9A84C" }} display="block" mb={0.5}>
                          AI Executive Summary
                        </VuiTypography>
                        <VuiTypography variant="button" color="text">{result.ai_summary}</VuiTypography>
                      </VuiBox>
                    </VuiBox>
                  </Card>
                )}

                {/* Issues list with filter tabs */}
                <Card>
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                    <VuiTypography variant="lg" color="white" fontWeight="bold">
                      {total === 0 ? "✓ No issues — data is clean" : `${total} Issue${total > 1 ? "s" : ""} Found`}
                    </VuiTypography>

                    {total > 0 && (
                      <VuiBox display="flex" gap={1} flexWrap="wrap">
                        {FILTERS.map((f) => (
                          <FilterTab key={f.label} {...f} active={filter === f.label} onClick={() => setFilter(f.label)} />
                        ))}
                      </VuiBox>
                    )}
                  </VuiBox>

                  {total === 0 && (
                    <VuiBox textAlign="center" py={3}>
                      <IoCheckmarkCircle size="48px" color="#48BB78" />
                      <VuiTypography variant="h6" color="success" mt={1}>
                        Your data is clean and ready for financial reporting.
                      </VuiTypography>
                    </VuiBox>
                  )}

                  {filteredIssues.length === 0 && total > 0 && (
                    <VuiTypography variant="caption" color="text">No {filter} issues found.</VuiTypography>
                  )}

                  {filteredIssues.map((issue, i) => <IssueRow key={i} issue={issue} />)}
                </Card>

              </VuiBox>
            )}
          </Grid>
        </Grid>


      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DataCleaner;
